// src/pages/RegisterPage.jsx
import React, { useState } from 'react';
import axiosInstance from '../axiosInstance';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  Avatar,
  Button,
  TextField,
  Link,
  Grid,
  Box,
  Typography,
  Paper,
  CssBaseline,
  CircularProgress,
  Fade,
  Divider,
  InputAdornment,
  Card,
  CardContent,
  Autocomplete,
  alpha
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';
import EmailIcon from '@mui/icons-material/Email';
import BadgeIcon from '@mui/icons-material/Badge';
import LockIcon from '@mui/icons-material/Lock';
import PersonIcon from '@mui/icons-material/Person';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import SearchIcon from '@mui/icons-material/Search';
import backgroundImage from '../assets/login.jpeg';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://app-medifyhc.com';

// Especialidades médicas agrupadas por categoría
const especialidadesMedicasAgrupadas = [
  {
    categoria: 'Medicina Interna y Especialidades',
    opciones: [
      "Medicina Interna",
      "Cardiología",
      "Endocrinología",
      "Gastroenterología",
      "Hematología",
      "Nefrología",
      "Neumología",
      "Neurología",
      "Oncología",
      "Reumatología",
      "Terapia Intensiva (Medicina Crítica)"
    ]
  },
  {
    categoria: 'Cirugía',
    opciones: [
      "Cirugía General",
      "Cirugía Cardiotorácica",
      "Cirugía Pediátrica",
      "Cirugía Plástica",
      "Cirugía Vascular",
      "Neurocirugía",
      "Ortopedia y Traumatología",
      "Urología"
    ]
  },
  {
    categoria: 'Especialidades Específicas',
    opciones: [
      "Administración Hospitalaria",
      "Alergología e Inmunología",
      "Algología",
      "Anestesiología",
      "Audiología",
      "Bioestadística Aplicada",
      "Biotecnología Médica",
      "Cuidados Paliativos",
      "Dermatología",
      "Epidemiología",
      "Farmacología Clínica",
      "Fisiología Humana",
      "Genética Médica",
      "Geriatría",
      "Ginecología y Obstetricia",
      "Infectología",
      "Kinesiología",
      "Maestría en Docencia Universitaria en Ciencias de la Salud",
      "Maestría en Economía de la Salud",
      "Maestría en Innovación en Salud Digital",
      "Maestría en Inteligencia Artificial Aplicada a la Medicina",
      "Maestría en Gestión Sanitaria y Calidad en Salud",
      "Maestría en Políticas Públicas de Salud",
      "Maestría en Telemedicina y eSalud",
      "Medicina Aeroespacial",
      "Medicina de Desastres y Catástrofes",
      "Medicina de Emergencia",
      "Medicina de Transporte y Evacuación Médica",
      "Medicina de Urgencias Pediátricas",
      "Medicina del Deporte",
      "Medicina del Dolor Intervencionista",
      "Medicina del Sueño",
      "Medicina del Trabajo",
      "Medicina Estética",
      "Medicina Familiar",
      "Medicina Física y Rehabilitación",
      "Medicina Forense",
      "Medicina General",
      "Medicina Hiperbárica",
      "Medicina Nuclear",
      "Medicina Preventiva",
      "Medicina Transfusional",
      "Medicina Tropical",
      "Neurociencias",
      "Neurofisiología Clínica",
      "Nutriología Clínica",
      "Oftalmología",
      "Otorrinolaringología",
      "Patología",
      "Pediatría",
      "Psiquiatría",
      "Radiología",
      "Salud Pública",
      "Terapia Física y Rehabilitación",
      "Toxicología Clínica"
    ]
  }
];

// Aplanar la lista para el Autocomplete
const especialidadesMedicas = especialidadesMedicasAgrupadas.flatMap(
  grupo => grupo.opciones
).sort();

const RegisterPage = () => {
  const theme = useTheme();
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    password2: '',
    especialidad_principal: '',
    numero_colegiado: ''
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // Manejador específico para el Autocomplete de especialidades
  const handleEspecialidadChange = (event, newValue) => {
    setFormData({ ...formData, especialidad_principal: newValue || '' });
  };

  // Utilidad para convertir un objeto de errores del backend a un string legible
  const buildErrorSummary = (errObj) => {
    try {
      if (!errObj || typeof errObj !== 'object') {
        return 'Ocurrió un error inesperado. Intenta de nuevo.';
      }
      // si es string plano
      if (typeof errObj === 'string') return errObj;

      // Algunos backends usan { non_field_errors: [...] }
      const entries = Object.entries(errObj);
      if (entries.length === 0) return 'Ocurrió un error. Verifica los campos.';

      const lines = [];
      for (const [field, detail] of entries) {
        // detail puede ser string o array de strings
        if (Array.isArray(detail)) {
          lines.push(`${field}: ${detail.join(', ')}`);
        } else if (typeof detail === 'string') {
          lines.push(`${field}: ${detail}`);
        } else if (typeof detail === 'object' && detail !== null) {
          // anidados (por si acaso)
          const inner = Object.entries(detail)
            .map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(', ') : String(v)}`)
            .join('; ');
          lines.push(`${field}: ${inner}`);
        } else {
          lines.push(`${field}: ${String(detail)}`);
        }
      }
      return lines.join('\n');
    } catch {
      return 'Ocurrió un error al procesar la respuesta del servidor.';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.password !== formData.password2) {
      toast.error("Las contraseñas no coinciden.");
      return;
    }

    setLoading(true);

    // Payload con los nombres de campo que el backend espera
    const payload = {
      username: formData.username,
      email: formData.email,
      especialidad_principal: formData.especialidad_principal,
      numero_colegiado: formData.numero_colegiado,
      password1: formData.password,
      password2: formData.password2
    };

    try {
      await axiosInstance.post('/auth/registration/', payload);
      toast.success('¡Registro de médico exitoso! Por favor, inicia sesión.');
      navigate('/login');
    } catch (err) {
      const serverErrors = err.response?.data || {};
      setErrors(serverErrors);

      // Construir y mostrar el resumen
      const summary = buildErrorSummary(serverErrors);

      // Tipificar errores comunes de forma más amigable (opcional)
      // Detectar duplicados típicos
      const lowerSummary = summary.toLowerCase();
      if (lowerSummary.includes('email') && (lowerSummary.includes('existe') || lowerSummary.includes('ya') || lowerSummary.includes('taken') || lowerSummary.includes('unique'))) {
        toast.error('El correo ya está registrado. Intenta con otro.');
      } else if (lowerSummary.includes('username') && (lowerSummary.includes('existe') ||
    lowerSummary.includes('ya') || lowerSummary.includes('taken') || lowerSummary.includes('unique'))) {
         toast.error('El nombre de usuario ya está registrado. Intenta con otro.');
      } else if (lowerSummary.includes('password') && lowerSummary.includes('no coincide')) {
        toast.error('Las contraseñas no coinciden.');
      } else {
        // Mostrar el resumen literal del backend si no cayó en los casos comunes
        toast.error(summary);
      }
    } finally {
      setLoading(false);
    }
  };


  // Helpers de estilo para dark mode friendly
  const glassBg = theme.palette.mode === 'dark'
    ? alpha(theme.palette.background.paper, 0.6)
    : 'rgba(255, 255, 255, 0.92)';

  const cardShadow = theme.palette.mode === 'dark'
    ? '0 10px 30px rgba(0,0,0,0.5)'
    : theme.shadows[8];

  const fieldBg = theme.palette.mode === 'dark'
    ? alpha(theme.palette.background.default, 0.6)
    : 'white';

  const heroOverlay = theme.palette.mode === 'dark'
    ? `linear-gradient(180deg, ${alpha(theme.palette.background.default, 0.7)}, ${alpha(theme.palette.background.default, 0.9)})`
    : `linear-gradient(180deg, ${alpha('#000', 0.15)}, ${alpha('#000', 0.25)})`;

  return (
    <Grid
      container
      component="main"
      sx={{
        minHeight: '100vh',
        width: '100vw',
        // Mobile friendly background: fixed on larger screens, cover always
        backgroundImage: {
          xs: `${heroOverlay}, url(${backgroundImage})`,
          sm: `${heroOverlay}, url(${backgroundImage})`
        },
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: { md: 'fixed' },
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        // Extra safe contrast in dark mode
        bgcolor: theme.palette.mode === 'dark' ? theme.palette.background.default : '#f5f5f5',
        p: { xs: 2, sm: 3 }
      }}
    >
      <CssBaseline />
      <Fade in={true} timeout={800}>
        <Card
          elevation={theme.palette.mode === 'dark' ? 0 : 8}
          sx={{
            width: '100%',
            maxWidth: 520,                        // más mobile friendly
            borderRadius: 3,
            overflow: 'visible',
            backgroundColor: glassBg,
            backdropFilter: 'blur(10px)',
            boxShadow: cardShadow,
            border: theme.palette.mode === 'dark' ? `1px solid ${alpha(theme.palette.common.white, 0.08)}` : 'none'
          }}
        >
          <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <Avatar
                sx={{
                  bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.7 : 0.9),
                  width: 56,
                  height: 56,
                  boxShadow: theme.palette.mode === 'dark' ? '0 6px 16px rgba(0,0,0,0.45)' : '0 2px 8px rgba(0,0,0,0.15)'
                }}
              >
                <MedicalServicesIcon fontSize="large" />
              </Avatar>
              <Box sx={{ ml: 2, minWidth: 0 }}>
                <Typography
                  component="h1"
                  variant="h5"
                  fontWeight="bold"
                  color="primary.main"
                  sx={{ lineHeight: 1.2 }}
                >
                  Registro de Médico
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 0.5 }}
                >
                  Crea tu cuenta para comenzar
                </Typography>
              </Box>
            </Box>

            <Divider
              sx={{
                mb: 3,
                borderColor: alpha(theme.palette.text.primary, 0.1)
              }}
            />

            {/* Form */}
            <Box component="form" noValidate onSubmit={handleSubmit} sx={{ mt: 1 }}>
              {/* Información de Usuario */}
              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                  <PersonIcon
                    sx={{
                      mr: 1,
                      color: 'primary.main',
                      fontSize: 20,
                      p: 0.5,
                      borderRadius: '50%',
                      backgroundColor: alpha(theme.palette.primary.main, 0.12)
                    }}
                  />
                  <Typography variant="subtitle1" fontWeight="medium" color="primary.main">
                    Información de Usuario
                  </Typography>
                </Box>

                <TextField
                  name="username"
                  label="Nombre de Usuario *"
                  placeholder="Usuario"
                  value={formData.username}
                  onChange={handleChange}
                  error={!!errors.username}
                  helperText={errors.username?.[0] || ' '}
                  required
                  fullWidth
                  margin="normal"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PersonIcon color="action" fontSize="small" />
                      </InputAdornment>
                    )
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '12px',
                      backgroundColor: fieldBg,
                      // Mejor foco para dark mode
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderWidth: '2px'
                      }
                    },
                    mb: 1
                  }}
                />

                <TextField
                  name="email"
                  label="Correo Electrónico *"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  error={!!errors.email}
                  helperText={errors.email?.[0] || ' '}
                  required
                  fullWidth
                  margin="normal"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <EmailIcon color="action" fontSize="small" />
                      </InputAdornment>
                    )
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '12px',
                      backgroundColor: fieldBg,
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderWidth: '2px'
                      }
                    }
                  }}
                />
              </Box>

              {/* Información Profesional */}
              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                  <LocalHospitalIcon
                    sx={{
                      mr: 1,
                      color: 'primary.main',
                      fontSize: 20,
                      p: 0.5,
                      borderRadius: '50%',
                      backgroundColor: alpha(theme.palette.primary.main, 0.12)
                    }}
                  />
                  <Typography variant="subtitle1" fontWeight="medium" color="primary.main">
                    Información Profesional
                  </Typography>
                </Box>

                {/* Autocomplete de especialidades (mobile-friendly + dark-friendly) */}
                <Autocomplete
                  id="especialidad-principal"
                  options={especialidadesMedicas}
                  value={formData.especialidad_principal}
                  onChange={handleEspecialidadChange}
                  freeSolo
                  fullWidth
                  autoHighlight
                  openOnFocus
                  blurOnSelect
                  clearOnEscape
                  groupBy={(option) => {
                    for (const grupo of especialidadesMedicasAgrupadas) {
                      if (grupo.opciones.includes(option)) {
                        return grupo.categoria;
                      }
                    }
                    return "Otras Especialidades";
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      name="especialidad_principal"
                      label="Especialidad Principal *"
                      placeholder="Buscar especialidad..."
                      required
                      error={!!errors.especialidad_principal}
                      helperText={errors.especialidad_principal?.[0] || ' '}
                      margin="normal"
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <>
                            <InputAdornment position="start">
                              <MedicalServicesIcon color="action" fontSize="small" />
                            </InputAdornment>
                            {params.InputProps.startAdornment}
                          </>
                        )
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '12px',
                          backgroundColor: fieldBg,
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderWidth: '2px'
                          }
                        },
                        mb: 1
                      }}
                    />
                  )}
                  sx={{
                    '& .MuiAutocomplete-paper': {
                      // Mejora contraste en dark mode
                      bgcolor: theme.palette.mode === 'dark'
                        ? alpha(theme.palette.background.paper, 0.98)
                        : theme.palette.background.paper,
                      backdropFilter: 'blur(6px)'
                    },
                    '& .MuiAutocomplete-listbox': {
                      '& .MuiAutocomplete-groupLabel': {
                        backgroundColor: alpha(theme.palette.primary.main, 0.1),
                        color: theme.palette.primary.main,
                        fontWeight: 'bold',
                        position: 'sticky',
                        top: 0,
                        zIndex: 1
                      }
                    }
                  }}
                />

                <TextField
                  name="numero_colegiado"
                  label="Número de Colegiado *"
                  value={formData.numero_colegiado}
                  onChange={handleChange}
                  error={!!errors.numero_colegiado}
                  helperText={errors.numero_colegiado?.[0] || ' '}
                  required
                  fullWidth
                  margin="normal"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <BadgeIcon color="action" fontSize="small" />
                      </InputAdornment>
                    )
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '12px',
                      backgroundColor: fieldBg,
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderWidth: '2px'
                      }
                    }
                  }}
                />
              </Box>

              {/* Seguridad */}
              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                  <LockIcon
                    sx={{
                      mr: 1,
                      color: 'primary.main',
                      fontSize: 20,
                      p: 0.5,
                      borderRadius: '50%',
                      backgroundColor: alpha(theme.palette.primary.main, 0.12)
                    }}
                  />
                  <Typography variant="subtitle1" fontWeight="medium" color="primary.main">
                    Seguridad
                  </Typography>
                </Box>

                <TextField
                  name="password"
                  label="Contraseña *"
                  type="password"
                  value={formData.password}
                  onChange={handleChange}
                  error={!!errors.password1}
                  helperText={errors.password1?.[0] || ' '}
                  required
                  fullWidth
                  margin="normal"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <VpnKeyIcon color="action" fontSize="small" />
                      </InputAdornment>
                    )
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '12px',
                      backgroundColor: fieldBg,
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderWidth: '2px'
                      }
                    },
                    mb: 1
                  }}
                />

                <TextField
                  name="password2"
                  label="Confirmar Contraseña *"
                  type="password"
                  value={formData.password2}
                  onChange={handleChange}
                  error={!!errors.password2}
                  helperText={errors.password2?.[0] || ' '}
                  required
                  fullWidth
                  margin="normal"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <VpnKeyIcon color="action" fontSize="small" />
                      </InputAdornment>
                    )
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '12px',
                      backgroundColor: fieldBg,
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderWidth: '2px'
                      }
                    }
                  }}
                />
              </Box>

              {/* CTA */}
              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={loading}
                sx={{
                  mt: 2,
                  mb: 2,
                  py: 1.4,
                  borderRadius: '14px',
                  boxShadow: 'none',
                  transition: 'all 0.2s ease',
                  fontWeight: 'bold',
                  fontSize: '1rem',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: theme.palette.mode === 'dark'
                      ? '0 8px 22px rgba(0,0,0,0.45)'
                      : '0 6px 16px rgba(25, 118, 210, 0.3)'
                  }
                }}
              >
                {loading ? <CircularProgress size={24} color="inherit" /> : 'Registrarse'}
              </Button>

              {/* Link a Login */}
              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <Button
                  component={RouterLink}
                  to="/login"
                  startIcon={<ArrowBackIcon />}
                  variant="tonal"
                  sx={{
                    color: theme.palette.primary.main,
                    backgroundColor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.12 : 0.06),
                    borderRadius: '20px',
                    px: 2,
                    py: 1,
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      transform: 'translateX(-3px)',
                      backgroundColor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.18 : 0.12)
                    }
                  }}
                >
                  ¿Ya tienes una cuenta? Inicia sesión
                </Button>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Fade>
    </Grid>
  );
};

export default RegisterPage;
